import { AnalyticsEventNameType } from "@coeff/api";
import { useCallback, useMemo, useRef, useState } from "react";

import { useTrack } from "../../utils";

import { useWebDashboardViewerContext } from "./WebDashboardViewerContext";

interface Params<TDraft extends object, TInstant extends object> {
  blockId: string;
  blockType: string;
  /** Deferred (text) field values as of mount — the baseline `isDirty` compares against. */
  initialDraft: TDraft;
  /**
   * Applies a click control's change locally (cache write, no save); the popover stays open.
   * Kept generic so the hook needs no import from the popovers that own the update shape
   * (which would be circular).
   */
  onApplyLocal: (patch: TInstant) => void;
  /** Applies the text draft (Done). */
  onDraftCommit: (draft: TDraft) => void;
}

interface Result<TDraft extends object, TInstant extends object> {
  draft: TDraft;
  setDraftField: <K extends keyof TDraft>(key: K, value: TDraft[K]) => void;
  isDirty: boolean;
  saveDraft: () => void;
  applyLocal: (patch: TInstant) => void;
}

/**
 * The two edit rhythms a block editor needs, in one place.
 *
 * Click controls (icon, chart type, legend, axes, ...) call `applyLocal` and the block
 * updates on the spot. Text controls (title, description) write to `draft` and are applied
 * only when `saveDraft` runs (Done), so `isDirty` tells Done whether there is text to fold in.
 * Neither path saves; the popover's session persists everything once when it closes.
 *
 * It also owns the analytics policy. `VibeDashBlockEdited` used to mean "the user pressed
 * Done", one event per session; firing it per click would inflate the metric by an order of
 * magnitude. So it fires AT MOST ONCE per popover session, on the first manual change of any
 * kind — keeping the event's original meaning.
 */
export function useBlockEditCommit<TDraft extends object, TInstant extends object>({
  blockId,
  blockType,
  initialDraft,
  onApplyLocal,
  onDraftCommit,
}: Params<TDraft, TInstant>): Result<TDraft, TInstant> {
  const track = useTrack();
  const { dashboardId, dashboardName, spreadsheetId } = useWebDashboardViewerContext();

  const [draft, setDraft] = useState<TDraft>(initialDraft);
  // Frozen at mount so isDirty compares against what the user opened, not the latest spec.
  const initialDraftRef = useRef(initialDraft);
  const hasTrackedRef = useRef(false);

  const trackOnce = useCallback(() => {
    if (hasTrackedRef.current) {
      return;
    }
    hasTrackedRef.current = true;
    track(AnalyticsEventNameType.VibeDashBlockEdited, {
      vibe_dash_id: dashboardId,
      ss_id: spreadsheetId,
      vibe_dash_name: dashboardName,
      vibe_dash_block_id: blockId,
      vibe_dash_block_type: blockType,
      vibe_dash_edit_method: "manual",
    });
  }, [track, dashboardId, spreadsheetId, dashboardName, blockId, blockType]);

  const setDraftField = useCallback(<K extends keyof TDraft>(key: K, value: TDraft[K]) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  }, []);

  const isDirty = useMemo(
    () =>
      (Object.keys(initialDraftRef.current) as Array<keyof TDraft>).some(
        key => draft[key] !== initialDraftRef.current[key]
      ),
    [draft]
  );

  const applyLocal = useCallback(
    (patch: TInstant) => {
      trackOnce();
      onApplyLocal(patch);
    },
    [trackOnce, onApplyLocal]
  );

  const saveDraft = useCallback(() => {
    trackOnce();
    onDraftCommit(draft);
  }, [trackOnce, onDraftCommit, draft]);

  return { draft, setDraftField, isDirty, saveDraft, applyLocal };
}
